import { useState, useMemo, type FormEvent } from "react";
import type { ShopVisit, Vehicle } from "@fleetlease/shared";
import { useList, useCreate, useUpdate, money } from "./data";
import { PageHeader, Button, Table, Modal, Field, Input, Select, useForm, FormError } from "./ui";
import { TableSkeleton } from "./ui";
import { Badge } from "./Badge";

const REFRESH = ["shop", "vehicles"];
const STATUSES = ["IN_SHOP", "READY_FOR_PICKUP", "RETURNED"];

export function Shop() {
  const today = new Date().toISOString().slice(0, 10);
  const visits = useList<ShopVisit>("shop", "/shop");
  const vehicles = useList<Vehicle>("vehicles", "/vehicles");
  const create = useCreate<any>("/shop", REFRESH);
  const update = useUpdate<any>((id) => `/shop/${id}`, REFRESH);
  const [editing, setEditing] = useState<ShopVisit | null>(null);
  const [open, setOpen] = useState(false);
  const blank = { vehicleId: "", workRequested: "", dropOffDate: today, expectedReturnDate: "", estimate: "", status: "IN_SHOP", actualReturnDate: "", final: "" };
  const f = useForm({ ...blank });
  const mut = editing ? update : create;

  const vById = useMemo(() => Object.fromEntries((vehicles.data?.items ?? []).map((v) => [v.id, v])), [vehicles.data]);
  const vname = (id: string) => { const v = vById[id]; return v ? `${v.year} ${v.make} ${v.model}` : id.slice(0, 8); };

  function openCreate() { setEditing(null); f.setValues({ ...blank }); setOpen(true); }
  function openEdit(s: ShopVisit) {
    setEditing(s);
    f.setValues({ ...blank, vehicleId: s.vehicleId, workRequested: s.workRequested, dropOffDate: s.dropOffDate, expectedReturnDate: s.expectedReturnDate ?? "", estimate: s.estimateCents ? String(s.estimateCents / 100) : "", status: s.status, actualReturnDate: s.actualReturnDate ?? "", final: s.finalCents ? String(s.finalCents / 100) : "" });
    setOpen(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (editing) {
      const body: any = { status: f.values.status };
      if (f.values.actualReturnDate) body.actualReturnDate = f.values.actualReturnDate;
      if (f.values.final) body.finalCents = Math.round(Number(f.values.final) * 100);
      await update.mutateAsync({ id: editing.id, body });
    } else {
      const body: any = {
        vehicleId: f.values.vehicleId, workRequested: f.values.workRequested,
        dropOffDate: f.values.dropOffDate, status: "IN_SHOP",
      };
      if (f.values.expectedReturnDate) body.expectedReturnDate = f.values.expectedReturnDate;
      if (f.values.estimate) body.estimateCents = Math.round(Number(f.values.estimate) * 100);
      await create.mutateAsync(body);
    }
    setOpen(false);
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Shop tracker — open visits" action={<Button onClick={openCreate}>Send to shop</Button>} />
      {visits.isPending ? <TableSkeleton cols={7} /> : (
        <Table<ShopVisit>
          rows={visits.data?.items ?? []} empty="No vehicles in the shop."
          columns={[
            { header: "Vehicle", cell: (v) => vname(v.vehicleId) },
            { header: "Work requested", cell: (v) => v.workRequested },
            { header: "Dropped off", cell: (v) => v.dropOffDate },
            { header: "Expected back", cell: (v) => v.expectedReturnDate ?? "—" },
            { header: "Estimate", cell: (v) => money(v.estimateCents) },
            { header: "Status", cell: (v) => <Badge tone={v.status === "IN_SHOP" ? "warn" : v.status === "READY_FOR_PICKUP" ? "info" : "info"}>{v.status}</Badge> },
            { header: "", cell: (v) => <button onClick={() => openEdit(v)} className="text-accent hover:underline text-sm">Update</button> },
          ]}
        />
      )}

      <Modal title={editing ? "Update shop visit" : "Send a vehicle to the shop"} open={open} onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <FormError error={mut.error} />
          {!editing ? (
            <>
              <Field label="Vehicle">
                <Select required value={f.values.vehicleId} onChange={(e) => f.set("vehicleId", e.target.value)}>
                  <option value="">Select a vehicle…</option>
                  {(vehicles.data?.items ?? []).filter((v) => (v as any).type === "Vehicle").map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} ({v.status})</option>)}
                </Select>
              </Field>
              <Field label="Work requested"><Input required value={f.values.workRequested} onChange={(e) => f.set("workRequested", e.target.value)} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Drop-off"><Input type="date" required value={f.values.dropOffDate} onChange={(e) => f.set("dropOffDate", e.target.value)} /></Field>
                <Field label="Expected back"><Input type="date" value={f.values.expectedReturnDate} onChange={(e) => f.set("expectedReturnDate", e.target.value)} /></Field>
                <Field label="Estimate ($)"><Input type="number" value={f.values.estimate} onChange={(e) => f.set("estimate", e.target.value)} /></Field>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 text-sm text-ink-muted">{vname(editing.vehicleId)} · {editing.workRequested}</div>
              <Field label="Status"><Select value={f.values.status} onChange={(e) => f.set("status", e.target.value)}>{STATUSES.map((x) => <option key={x}>{x}</option>)}</Select></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Actual return date"><Input type="date" value={f.values.actualReturnDate} onChange={(e) => f.set("actualReturnDate", e.target.value)} /></Field>
                <Field label="Final cost ($)"><Input type="number" value={f.values.final} onChange={(e) => f.set("final", e.target.value)} /></Field>
              </div>
              <p className="text-xs text-ink-muted">Setting status to RETURNED frees the vehicle back to AVAILABLE.</p>
            </>
          )}
          <div className="mt-3 flex justify-end"><Button type="submit" disabled={mut.isPending}>{mut.isPending ? "Saving…" : editing ? "Save" : "Send to shop"}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
